import React, { useState, useEffect, useCallback } from 'react';
import Input from './Input';
import CountryCodeSelector from './CountryCodeSelector';
import { CountryCode, DEFAULT_COUNTRY, findCountryByCode } from '../../data/countryCodes';
import { validatePhoneClientSide } from '../../utils/phoneValidationV2';
import { phoneValidationAPI } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { Phone, XCircle } from 'lucide-react';

export interface PhoneValidationResponse {
  valid: boolean;
  normalized: string;
  country: string;
  input_country?: string;
  error: string;
  info?: {
    e164: string;
    international: string;
    country_code: string;
  };
}

interface ErrorObject {
  type?: string;
  message?: string;
  details?: any;
}

export interface PhoneInputWithValidationProps {
  value: string;
  onChange: (phone: string, countryCode: string) => void;
  onValidationChange?: (validation: PhoneValidationResponse | null) => void;
  error?: string | ErrorObject | null;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  showValidationResult?: boolean;
  className?: string;
  debounceMs?: number;
  dropdownZIndex?: number;
  initialCountryCode?: string;  // ISO country code (e.g., 'US', 'JO')
}

/**
 * Reusable phone input component with backend validation
 * 
 * Features:
 * - Country code selector
 * - Basic frontend validation (prevents garbage input)
 * - Backend validation (authoritative)
 * - Debounced validation for better UX
 * - Shows formatted result and errors
 */
export const PhoneInputWithValidation: React.FC<PhoneInputWithValidationProps> = ({
  value,
  onChange,
  onValidationChange,
  error: externalError,
  placeholder,
  disabled = false,
  required = false,
  showValidationResult = true,
  className = '',
  debounceMs = 500,
  dropdownZIndex,
  initialCountryCode
}) => {
  const [phone, setPhone] = useState(value);
  // Initialize with initialCountryCode if provided, otherwise use DEFAULT_COUNTRY
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() => {
    if (initialCountryCode) {
      const country = findCountryByCode(initialCountryCode);
      if (country) return country;
    }
    return DEFAULT_COUNTRY;
  });
  const [frontendError, setFrontendError] = useState<string | null>(null);
  const [backendValidation, setBackendValidation] = useState<PhoneValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  // Update selected country when initialCountryCode prop changes
  useEffect(() => {
    if (initialCountryCode) {
      const country = findCountryByCode(initialCountryCode);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [initialCountryCode]);

  // Debounce phone value for backend validation
  const debouncedPhone = useDebounce(phone, debounceMs);

  // Update local state when external value changes
  useEffect(() => {
    setPhone(value);
  }, [value]);

  // Backend validation function
  const validateWithBackend = useCallback(async (phoneValue: string, countryCode: string) => {
    if (!phoneValue || !phoneValue.trim()) {
      setBackendValidation(null);
      onValidationChange?.(null);
      return;
    }

    setIsValidating(true);
    try {
      const result = await phoneValidationAPI.validatePhone(phoneValue, countryCode);
      
      if (result.success && result.data) {
        const validation = result.data;
        
        // Check for country mismatch - reject validation if detected country doesn't match selected
        if (validation.valid && 
            validation.country && 
            validation.input_country && 
            validation.country !== validation.input_country) {
          // Mark as invalid due to country mismatch
          const mismatchError = `This number belongs to ${validation.country}, but you selected ${validation.input_country}. Please select the correct country from the dropdown.`;
          
          setBackendValidation({
            valid: false,
            normalized: '',
            country: validation.country,
            input_country: validation.input_country,
            error: mismatchError
          });
          
          setFrontendError(mismatchError);
          onValidationChange?.(null);
          setIsValidating(false);
          return;
        }
        
        // Normal validation - no country mismatch
        setBackendValidation(validation);
        onValidationChange?.(validation);
        // Clear frontend error if backend validation passes
        if (validation.valid) {
          setFrontendError(null);
        } else {
          setFrontendError(validation.error || 'Invalid phone number');
        }
      } else {
        setBackendValidation({
          valid: false,
          normalized: '',
          country: countryCode,
          error: result.error || 'Validation failed'
        });
        onValidationChange?.(null);
        setFrontendError(result.error || 'Validation failed');
      }
    } catch (err: any) {
      setBackendValidation({
        valid: false,
        normalized: '',
        country: countryCode,
        error: err.message || 'Validation error'
      });
      onValidationChange?.(null);
      setFrontendError('Failed to validate phone number');
    } finally {
      setIsValidating(false);
    }
  }, [onValidationChange]);

  // Frontend validation (basic checks only)
  useEffect(() => {
    if (!debouncedPhone || !debouncedPhone.trim()) {
      setFrontendError(required ? 'Phone number is required' : null);
      setBackendValidation(null);
      onValidationChange?.(null);
      return;
    }

    // Basic client-side validation
    const validation = validatePhoneClientSide(debouncedPhone, selectedCountry.dialCode);
    if (!validation.isValid) {
      setFrontendError(validation.error || 'Invalid phone number format');
      setBackendValidation(null);
      onValidationChange?.(null);
    } else {
      setFrontendError(null);
      // Trigger backend validation if frontend validation passes
      validateWithBackend(debouncedPhone, selectedCountry.code);
    }
  }, [debouncedPhone, selectedCountry, required, validateWithBackend, onValidationChange]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
    onChange(newPhone, selectedCountry.code);
    setHasBlurred(false);
    // Clear errors when user starts typing
    if (frontendError) {
      setFrontendError(null);
    }
  };

  const handleCountryChange = (country: CountryCode) => {
    setSelectedCountry(country);
    // Clear phone input when country changes to prevent confusion
    // This ensures user enters a number that matches the selected country
    setPhone('');
    setBackendValidation(null);
    setFrontendError(null);
    onChange('', country.code);
    onValidationChange?.(null);
  };

  const handleBlur = () => {
    setHasBlurred(true);
    // Trigger validation on blur if not already validated
    if (phone && phone.trim() && !backendValidation) {
      validateWithBackend(phone, selectedCountry.code);
    }
  };

  // Determine which error to show - extract message if it's an object
  const getErrorMessage = (err: string | ErrorObject | null | undefined): string | null => {
    if (!err) return null;
    if (typeof err === 'string') return err;
    return err?.message || err?.details || null;
  };
  
  const displayError = getErrorMessage(externalError) || (hasBlurred ? frontendError : null) || (backendValidation && !backendValidation.valid ? backendValidation.error : null);

  return (
    <div className={`w-full ${className}`}>
      {/* items-start keeps country + field top-aligned when an error appears under the field */}
      <div className="flex gap-1.5 w-full items-start">
        <div className="flex-shrink-0 h-14">
          <CountryCodeSelector
            selectedCountry={selectedCountry}
            onCountryChange={handleCountryChange}
            className="w-28"
            disabled={disabled}
            dropdownZIndex={dropdownZIndex}
          />
        </div>
        <div className="flex-1 relative min-w-0">
          <Input
            type="tel"
            value={phone}
            onChange={handlePhoneChange}
            onBlur={handleBlur}
            error={displayError || undefined}
            hideErrorMessage
            placeholder={placeholder || 'Enter phone number'}
            disabled={disabled}
            leftIcon={<Phone className="h-5 w-5" />}
            inputSize="lg"
            className="w-full h-14"
          />
          {isValidating && (
            <div className="absolute right-3 top-7 -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>
          )}
          {/*
            Reserved slot under the phone field only — error paints here without
            growing the Input or vertically re-centering the country selector.
          */}
          <div
            className="min-h-[2.75rem] mt-1"
            aria-live="polite"
          >
            {displayError && (
              <div className="flex items-start gap-1.5">
                <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-600 dark:text-red-400 font-medium leading-snug">
                  {displayError}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Validation Result Display */}
      {showValidationResult && backendValidation && (
        <div className={`p-3 rounded-lg border ${
          backendValidation.valid
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          {backendValidation.valid ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
                <span>✓</span>
                <span>Valid phone number</span>
              </div>
              {backendValidation.info && (
                <div className="text-xs text-green-600 dark:text-green-400 space-y-0.5 ml-6">
                  <div>
                    <span className="font-medium">Formatted (E.164):</span>{' '}
                    <code className="bg-green-100 dark:bg-green-900/30 px-1 rounded">
                      {backendValidation.normalized}
                    </code>
                  </div>
                  <div>
                    <span className="font-medium">International format:</span>{' '}
                    <code className="bg-green-100 dark:bg-green-900/30 px-1 rounded">
                      {backendValidation.info.international}
                    </code>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-red-700 dark:text-red-300">
              <div className="flex items-center gap-2 font-medium">
                <span>✗</span>
                <span>Invalid phone number</span>
              </div>
              {backendValidation.error && (
                <div className="mt-1 ml-6 text-xs">
                  {backendValidation.error}
                </div>
              )}
            </div>
          )}
          
          {/* Country Mismatch Error - Show prominently when validation fails due to mismatch */}
          {backendValidation && 
           !backendValidation.valid && 
           backendValidation.country && 
           backendValidation.input_country && 
           backendValidation.country !== backendValidation.input_country && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg">
              <div className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-700 dark:text-red-300 text-sm">
                    Country Mismatch Detected
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    This number belongs to <strong>{backendValidation.country}</strong> but you selected{' '}
                    <strong>{backendValidation.input_country}</strong>. Please select the correct country from the dropdown above.
                  </p>
                  {findCountryByCode(backendValidation.country) && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-2 font-medium">
                      💡 Tip: Select {findCountryByCode(backendValidation.country)?.name} ({findCountryByCode(backendValidation.country)?.dialCode}) from the country dropdown
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

